import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { errorManager, userManager, userStatusManager } from "@core/service-container";
import { useUser } from "@domain/user/hooks/use-user";
import { RegistrationStatus } from "@domain/user/user";
import styled from "@emotion/styled";
import { DialogProvider } from "@ui/components/dialog/dialog-provider";
import { LegalContent } from "@ui/components/legal-content/legal-content";
import { ToastContext } from "@ui/components/common/toast-provider";
import { TutorialBannerDisplayer } from "@ui/components/tutorial/tutorial-banner-displayer";
import { WindowFocusHandler } from "@ui/components/common/window-focus-handler";
import { CloseDialogHandler } from "@ui/navigation/close-dialog-handler";
import { RoutesPath } from "@ui/navigation/routes";
import { TabBar } from "@ui/navigation/tab-bar/tab-bar";
import { AnnouncementHandler } from "@ui/pages/announcement-dialog/announcement-handler";
import { GetStartedCompletedHandler } from "@ui/pages/get-started-completed-dialog/get-started-completed-handler";
import { useNotificationToast } from "@ui/pages/notification-center/hooks/use-notification-toast";
import { stack } from "@ui/style";
import { isNil, isNotNil } from "@utils/assert";
import { isLocationMatchingRoutes } from "@utils/router-utils";
import { useObservable } from "micro-observables";
import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { ScrollRestoration, useSearchParams } from "react-router-dom";

const routesAvailableWithoutConsent: string[] = [
	RoutesPath.TermsOfUse,
	RoutesPath.PrivacyNotice,
	RoutesPath.Debug,
	RoutesPath.Login,
	RoutesPath.VipLogin,
];

const routesWithoutTabbars: RoutesPath[] = [
	RoutesPath.TemplateBuilder,
	RoutesPath.ProgramBuilder,
	RoutesPath.ProfessionalInformation,
];

export const AuthenticatedRoutes = (): JSX.Element => {
	const ptRegistrationStatus = useObservable(userStatusManager.ptRegistrationStatus);
	const user = useUser();
	const location = useLocation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { format } = useI18n();
	const { showToast } = useContext(ToastContext);

	const showTabBar = useMemo(() => {
		if (!user?.hasAgreedToLegalDocuments) return false;
		if (ptRegistrationStatus === RegistrationStatus.REJECTED) return false;
		if (isLocationMatchingRoutes(location, routesWithoutTabbars)) return false;
		return true;
	}, [location, ptRegistrationStatus, user?.hasAgreedToLegalDocuments]);

	useEffect(() => {
		if (isNil(user?.id)) {
			navigate(RoutesPath.Login, {
				query: {
					patientId: searchParams.get("patientId") ?? undefined,
					ptId: searchParams.get("ptId") ?? undefined,
					lang: searchParams.get("lang") ?? undefined,
				},
			});
			return;
		}
		if (ptRegistrationStatus === RegistrationStatus.REJECTED) {
			navigate(RoutesPath.Rejected);
		}
		// remove navigate from dependencies 'cause navigate is refresh at each navigate
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, ptRegistrationStatus, searchParams, user?.id]);

	useEffect(() => {
		const navigateToHomePageWithToast = () => {
			navigate(RoutesPath.Home);
			showToast("", format("patientDetails.errorToast.notInHealthcareTeam"));
		};

		errorManager.onPtNotInHealthcareTeam.add(navigateToHomePageWithToast);
		return () => errorManager.onPtNotInHealthcareTeam.remove(navigateToHomePageWithToast);
	}, [format, navigate, showToast]);

	const onWindowFocus = useCallback(async () => {
		await userManager.retrieveUser();
	}, []);

	useNotificationToast();

	return (
		<DialogProvider>
			<ScrollRestoration />
			<CloseDialogHandler />
			<AnnouncementHandler />
			<GetStartedCompletedHandler />
			{ptRegistrationStatus && (
				<Content>
					<WindowFocusHandler onFocus={onWindowFocus}>
						<TabBar displayTabs={showTabBar}>
							{user?.hasAgreedToLegalDocuments || routesAvailableWithoutConsent.includes(location.pathname) ? (
								<>
									<TutorialBannerDisplayer />
									<Outlet />
								</>
							) : (
								<LegalContent />
							)}
						</TabBar>
					</WindowFocusHandler>
				</Content>
			)}
		</DialogProvider>
	);
};

export const UnAuthenticatedRoutes = (): JSX.Element => {
	const user = useUser();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { clearToasts } = useContext(ToastContext);

	useEffect(() => {
		if (isNotNil(user)) {
			const patientId = searchParams.get("patientId");
			const ptId = searchParams.get("ptId");
			navigate(RoutesPath.Home, {
				query: { patientId: patientId ?? undefined, ptId: ptId ?? undefined },
			});
		}

		// clear toast when not authenticated.
		clearToasts();
	}, [clearToasts, navigate, searchParams, user]);

	useEffect(() => {
		const userDeletingInfos = userManager.userDeletingInfos;
		if (isNotNil(userDeletingInfos)) {
			navigate(RoutesPath.AccountDeleted, { state: userDeletingInfos });
		}
	}, [navigate]);

	return (
		<DialogProvider>
			<ScrollRestoration />
			{!user && <Outlet />}
		</DialogProvider>
	);
};

export const DebugRoutes = (): JSX.Element => {
	const user = useUser();

	return user?.isTester ? <Outlet /> : <Navigate to={RoutesPath.Home} />;
};

const Content = styled.div`
	${stack(undefined, "flexstart", "stretch")}
	min-height: 100vh;
`;
